import { useState, VFC } from 'react'
import { useForm } from 'react-hook-form'
import { MasterDataWithCategory } from 'src/models/MasterData'
import {
  _formalert,
  _menuicon,
  bordergray,
  orange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled, { css } from 'styled-components'
import {
  Control,
  DeselectButton,
  SelectableItemDiv,
  SelectableItems,
} from './common'

const transitionTime = '0.3s'

// NOTE: Features専用のアコーディオン形式表示コンポーネント（subCategoryでグループ化）
export const FeaturesSelection: VFC<{
  title: string
  description: string
  items: MasterDataWithCategory[]
  selectedItems: MasterDataWithCategory[]
  setSelectedItems: (selectedItems: MasterDataWithCategory[]) => void
  handleOnSubmit: () => void
  errorMessages: string
}> = ({
  title,
  description,
  items,
  selectedItems,
  setSelectedItems,
  handleOnSubmit,
  errorMessages,
}) => {
  const { handleSubmit } = useForm()

  const updateItems = (selected: MasterDataWithCategory, checked: boolean) => {
    checked
      ? setSelectedItems([...selectedItems, selected])
      : setSelectedItems(
          selectedItems.filter((each) => each.value !== selected.value),
        )
  }

  // NOTE: categoryごとにグループ化（タブとして表示）
  const categories = Array.from(new Set(items.map((s) => s.category))).filter(
    (v) => v,
  )
  const [selectedCategory, setSelectedCategory] = useState(categories[0])

  // NOTE: 選択中のカテゴリーのアイテムをフィルタリング
  const filteredItems = items.filter((item) =>
    selectedCategory ? item.category === selectedCategory : item,
  )

  // NOTE: 選択中のカテゴリー内でsubCategoryごとにグループ化
  const getCategorySections = (categoryItems: MasterDataWithCategory[]) => {
    const grouped = categoryItems.reduce<{
      [key: string]: MasterDataWithCategory[]
    }>((acc, item) => {
      const key = item.subCategory || ''
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    }, {})

    const subCategories = Object.keys(grouped)

    return subCategories.map((subCategory) => ({
      subCategory,
      items: grouped[subCategory],
    }))
  }

  const selectAll = () => {
    // NOTE: 現在のカテゴリー内のアイテムをすべて選択
    const array = [...selectedItems, ...filteredItems]
    // NOTE: 重複削除
    const filteredArray = array.filter(
      (arrayItem, index, self) =>
        self.findIndex((selfItem) => selfItem.label === arrayItem.label) ===
        index,
    )
    setSelectedItems(filteredArray)
  }

  const deselectAll = () => {
    // NOTE: selectedItemsからfilteredItemsを削除
    const array = selectedItems.filter(
      (item) => !filteredItems.map((item) => item.label).includes(item.label),
    )
    setSelectedItems(array)
  }

  const getBorderRadiusCss = (index: number, length: number) => {
    if (index === 0) return '16px 0 0 0 ;'
    else if (index + 1 === length) return '0 0 0 16px;'
    else return ''
  }

  const sections = getCategorySections(filteredItems)

  return (
    <Layout>
      <TitleDiv>
        <h1>{`${title}登録`}</h1>
        <p>{description}</p>
      </TitleDiv>
      <SelectionDiv>
        <FlexDiv>
          <FieldTypeDiv>
            {categories.map((category, index, array) => (
              <FieldTypeButton
                key={index}
                type={'button'}
                onClick={() => setSelectedCategory(category)}
                selected={category === selectedCategory}
                borderRadius={getBorderRadiusCss(index, array.length)}
                height={100 / array.length}
              >
                {categories[index]}
              </FieldTypeButton>
            ))}
          </FieldTypeDiv>
          <SpecialtyDiv withCategory={!!categories.length}>
            <h1>{selectedCategory || title}を選択</h1>
            <ControlWrapper>
              <Control>
                <button type="button" onClick={() => selectAll()}>
                  すべて選択
                </button>
                <DeselectButton type="button" onClick={() => deselectAll()}>
                  解除
                </DeselectButton>
              </Control>
            </ControlWrapper>
            <CategoryContainer>
              {sections.map(({ subCategory, items: sectionItems }) =>
                subCategory ? (
                  <SubCategoryAccordion
                    key={`${selectedCategory}_${subCategory}`}
                    category={selectedCategory}
                    subCategory={subCategory}
                    items={sectionItems}
                    selectedItems={selectedItems}
                    updateItems={updateItems}
                    setSelectedItems={setSelectedItems}
                  />
                ) : (
                  <DirectItemsList
                    key={`${selectedCategory}_direct`}
                    items={sectionItems}
                    selectedItems={selectedItems}
                    updateItems={updateItems}
                  />
                ),
              )}
            </CategoryContainer>
          </SpecialtyDiv>
        </FlexDiv>
      </SelectionDiv>
      <SaveButtonDiv>
        <SaveButton
          type={'button'}
          onClick={handleSubmit(handleOnSubmit)}
          disabled={!!errorMessages}
        >
          保存する
        </SaveButton>
        <ErrorMessages>
          <p>{errorMessages}</p>
        </ErrorMessages>
      </SaveButtonDiv>
    </Layout>
  )
}

// ========================
// Styled Components for Main Layout
// ========================

const ControlWrapper = styled.div`
  flex-shrink: 0;
`

const Layout = styled.div`
  border-radius: 16px;
  width: 928px;
  max-width: min(80vw, 928px);
  text-align: center;
  white-space: pre-wrap;
  line-height: 1.4;

  @media screen and (max-width: 960px) {
    max-width: min(90vw, 700px);
  }
`

const TitleDiv = styled.div`
  display: flex;
  margin: 30px auto;
  padding: 22.5px 40px;
  border-radius: 16px;
  background-color: ${truewhite};
  text-align: left;

  h1 {
    font-size: 24px;
    font-weight: ${fontWeightBold};
    line-height: 20px;
    white-space: nowrap;
  }

  p {
    font-size: 13px;
    line-height: 20px;
    padding-left: 40px;
    white-space: pre-wrap;
  }

  @media screen and (max-width: 960px) {
    padding: 15px 20px;
    margin-bottom: 20px;

    h1 {
      font-size: 20px;
      line-height: 20px;
    }

    p {
      padding-left: 20px;
    }
  }

  @media screen and (max-width: 520px) {
    display: block;
    padding: 10px 15px;
    margin-bottom: 20px;

    h1 {
      font-size: 16px;
      line-height: 16px;
    }

    p {
      font-size: 12px;
      margin-top: 5px;
      padding-left: 0px;
    }
  }
`

const SaveButtonDiv = styled.div`
  display: flex;
  margin: 30px auto;
  padding: 20px 40px;
  line-height: 1.4;
  border-radius: 16px;
  background-color: ${truewhite};
  white-space: pre-wrap;
  text-align: left;

  @media screen and (max-width: 960px) {
    padding: 15px 20px;
    margin-top: 20px;
    font-size: 12px;
  }

  @media screen and (max-width: 520px) {
    padding: 10px 15px;
  }
`

const SelectionDiv = styled.div`
  background-color: ${truewhite};
  border-radius: 16px;
  text-align: center;
  white-space: pre-wrap;
  line-height: 1.4;
`

const FlexDiv = styled.div`
  display: flex;
  min-height: 250px;
  max-height: 500px;
  height: 500px;
  overflow: hidden;
`

const FieldTypeDiv = styled.div`
  max-width: 200px;
`

const FieldTypeButton = styled.button<{
  selected: boolean
  borderRadius: string
  height: number
}>`
  display: block;
  width: 100%;
  height: ${(props) => `${props.height}%`};
  margin: 0 auto;
  padding: 15px 20px;
  font-weight: ${fontWeightBold};
  background-color: ${(props) => (props.selected ? `${orange}` : 'white')};
  color: ${(props) => (props.selected ? 'white' : 'black')};
  border-radius: ${(props) => props.borderRadius};
  border: 1px solid ${_menuicon};
  box-shadow: 1px 1px 5px 0.2px #999;
  white-space: nowrap;

  @media screen and (max-width: 960px) {
    font-size: 12px;
    padding: 10px;
  }

  @media screen and (max-width: 520px) {
    font-size: 10px;
    padding: 8px;
  }
`

const SpecialtyDiv = styled.div<{ withCategory: boolean }>`
  flex-grow: 1;
  padding: 16px 24px;
  text-align: left;
  display: flex;
  flex-direction: column;
  max-height: 500px;

  h1 {
    font-size: 22px;
    color: ${_menuicon};
    flex-shrink: 0;
  }

  @media screen and (max-width: 960px) {
    padding: 12px 16px;
    max-height: 450px;

    h1 {
      font-size: 18px;
    }
  }

  @media screen and (max-width: 520px) {
    padding: 8px 12px;
    max-height: 400px;

    h1 {
      font-size: 16px;
    }
  }
`

const CategoryContainer = styled.div`
  margin-top: 8px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  position: relative;
`

const DirectItemsContainer = styled.div`
  margin-top: 12px;
`

const SaveButton = styled.button`
  color: ${orange};
  background-color: #ff6c0033;
  padding: 10px 40px;
  border: 1px solid ${orange};
  border-radius: 5px;
`

const ErrorMessages = styled.div`
  color: ${_formalert};
  text-align: center;
  padding: 10px 40px;
`

// ========================
// Styled Components for Accordion
// ========================

const ChecklabelHeader = styled.div<{ opened: boolean }>`
  position: relative;
  padding: 10px;
  font-size: 16px;
  font-weight: ${fontWeightBold};
  cursor: pointer;
  margin-top: 12px;

  ${({ opened }) =>
    // NOTE: transition-delayはaccordionをcloseし終わったらborder-bottomを表示するため
    !opened &&
    css`
      border-bottom: 1px solid ${bordergray};
      transition-delay: ${transitionTime};
      padding-bottom: 20px;
    `}
`

const HeaderLabel = styled.div<{ opened: boolean }>`
  cursor: pointer;
  input {
    transform: translate(0px, -2px);
  }
  label {
    margin-left: 3px;
  }
  label::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 17px;
    right: 15px;
    width: 8px;
    height: 8px;
    border-right: 2px solid ${_menuicon};
    border-bottom: 2px solid ${_menuicon};
    transform: ${({ opened }) =>
      opened ? 'rotate(-135deg)' : 'rotate(45deg)'};
    transition: transform ${transitionTime};
  }
`

const CheckboxDiv = styled.div<{ color: string }>`
  margin-bottom: 4px;

  input[type='checkbox'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    -ms-appearance: none;
    -o-appearance: none;
    appearance: none;
    position: relative;
    right: 0;
    bottom: 0;
    left: 0;
    height: 18px;
    width: 18px;
    color: ${({ color }) => color};
    cursor: pointer;
    display: inline-block;
    outline: none;
    border-radius: 3px;
    border: 1px solid #e2e2e2;
  }

  input[type='checkbox']:before,
  input[type='checkbox']:after {
    position: absolute;
    content: '';
    background: ${({ color }) => color};
  }

  input[type='checkbox']:before {
    left: 2px;
    top: 6px;
    width: 0;
    height: 2px;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    -moz-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    -o-transform: rotate(45deg);
  }

  input[type='checkbox']:after {
    right: 9px;
    bottom: 3px;
    width: 2px;
    height: 0;
    transform: rotate(40deg);
    -webkit-transform: rotate(40deg);
    -moz-transform: rotate(40deg);
    -ms-transform: rotate(40deg);
    -o-transform: rotate(40deg);
  }

  input[type='checkbox']:checked:before {
    left: 1px;
    top: 9px;
    width: 6px;
    height: 3px;
  }

  input[type='checkbox']:checked:after {
    right: 4px;
    bottom: 0px;
    width: 3px;
    height: 14px;
  }

  input[type='checkbox']:checked,
  input[type='checkbox']:indeterminate {
    border: 1px solid #e2e2e2;
    background: ${truewhite};
  }

  input[type='checkbox']:indeterminate:before,
  input[type='checkbox']:indeterminate:after {
    width: 7px;
    height: 2px;
    transform: rotate(0);
    -webkit-transform: rotate(0);
    -moz-transform: rotate(0);
    -ms-transform: rotate(0);
    -o-transform: rotate(0);
  }

  input[type='checkbox']:indeterminate:before {
    left: 1px;
    top: 7px;
  }

  input[type='checkbox']:indeterminate:after {
    right: 1px;
    bottom: 7px;
  }

  label {
    cursor: pointer;
  }
`

const Accordion = styled.div<{ opened: boolean }>`
  transition: ${transitionTime};
  ${({ opened }) =>
    opened
      ? css`
          border-bottom: 1px solid ${bordergray};
          padding-bottom: 10px;
          max-height: none;
          visibility: visible;
        `
      : css`
          max-height: 0px;
          visibility: hidden;
        `}
`

// ========================
// Sub Components
// ========================

// サブカテゴリーがない場合の直接表示コンポーネント
const DirectItemsList: VFC<{
  items: MasterDataWithCategory[]
  selectedItems: MasterDataWithCategory[]
  updateItems: (selected: MasterDataWithCategory, checked: boolean) => void
}> = ({ items, selectedItems, updateItems }) => {
  return (
    <DirectItemsContainer>
      <SelectableItems>
        {items?.map((option) => (
          <SelectableItemDiv key={option.value}>
            <input
              id={`feature_${option.value}`}
              type="checkbox"
              onChange={({ target: { checked } }) =>
                updateItems(option, checked)
              }
              checked={selectedItems.some(
                ({ value }) => option.value === value,
              )}
            />
            <label htmlFor={`feature_${option.value}`}>{option.label}</label>
          </SelectableItemDiv>
        ))}
      </SelectableItems>
    </DirectItemsContainer>
  )
}

// サブカテゴリーごとのアコーディオンコンポーネント
const SubCategoryAccordion: VFC<{
  category: string
  subCategory: string
  items: MasterDataWithCategory[]
  selectedItems: MasterDataWithCategory[]
  updateItems: (selected: MasterDataWithCategory, checked: boolean) => void
  setSelectedItems: (items: MasterDataWithCategory[]) => void
}> = ({
  category,
  subCategory,
  items,
  selectedItems,
  updateItems,
  setSelectedItems,
}) => {
  const [opened, setOpened] = useState(true)

  const displayLabel = subCategory || category

  const updateSelectedCategory = (checked: boolean) => {
    if (checked) {
      // カテゴリ内のアイテムを追加（重複を除外）
      const addSelectedItems = [
        ...selectedItems,
        ...items.filter(
          (item) => !selectedItems.some((s) => s.value === item.value),
        ),
      ]
      setSelectedItems(addSelectedItems)
    } else {
      // カテゴリ内のアイテムを除外
      const excludedItems = selectedItems.filter((item) => {
        const categoryItem = items.find((i) => i.value === item.value)
        if (!categoryItem) return true
        return item.value !== categoryItem.value
      })
      setSelectedItems(excludedItems)
    }
  }

  // カテゴリ内のすべてのアイテムがチェック済みかどうか
  const allChecked = items?.every(({ value }) => {
    const checked = selectedItems.find((selected) => selected.value === value)
    if (!checked) return false
    return checked.value === value
  })

  // カテゴリ内の一部のアイテムがチェック済みかどうか
  const someChecked = items?.some(({ value }) => {
    return selectedItems.some((selected) => selected.value === value)
  })

  const isIndeterminate = someChecked && !allChecked

  return (
    <>
      <ChecklabelHeader onClick={() => setOpened(!opened)} opened={opened}>
        <HeaderLabel opened={opened}>
          <CheckboxDiv color={orange}>
            <input
              id={`feature_${displayLabel}`}
              type="checkbox"
              ref={(input) => {
                if (input) input.indeterminate = isIndeterminate
              }}
              onChange={({ target: { checked } }) =>
                updateSelectedCategory(checked)
              }
              onClick={(e) => e.stopPropagation()}
              checked={allChecked}
            />
            <label
              htmlFor={`feature_${displayLabel}`}
              onClick={(e) => e.stopPropagation()}
            >
              {displayLabel}
              {someChecked && !allChecked && (
                <span
                  style={{
                    marginLeft: '8px',
                    color: '#a8a9ac',
                    fontSize: '14px',
                  }}
                >
                  (
                  {
                    items.filter((item) =>
                      selectedItems.some((s) => s.value === item.value),
                    ).length
                  }
                  /{items.length})
                </span>
              )}
            </label>
          </CheckboxDiv>
        </HeaderLabel>
      </ChecklabelHeader>
      <Accordion opened={opened}>
        <SelectableItems>
          {items?.map((option) => (
            <SelectableItemDiv key={option.value}>
              <input
                id={`feature_${option.value}`}
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  updateItems(option, checked)
                }
                checked={selectedItems.some(
                  ({ value }) => option.value === value,
                )}
              />
              <label htmlFor={`feature_${option.value}`}>{option.label}</label>
            </SelectableItemDiv>
          ))}
        </SelectableItems>
      </Accordion>
    </>
  )
}
